import React from 'react';
import { Building2, Briefcase, FileText, MapPin } from 'lucide-react';
import { Select } from '@/components/ui/Select';
import { LocationInput } from '@/components/common/LocationInput';
import { TargetFieldWithReference } from './TargetFieldWithReference';
import { TargetDetails } from '@/types/workspace';

interface TargetDetailsFormProps {
  details: TargetDetails;
  onChange: (details: TargetDetails) => void;
  isExtracting?: boolean | 'fetching';
}

export const TargetDetailsForm = React.memo(function TargetDetailsForm({ details, onChange, isExtracting }: TargetDetailsFormProps) {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onChange({ ...details, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (name: string, value: string) => {
    onChange({ ...details, [name]: value });
  };

  return (
    <div className="flex flex-col gap-4 w-full relative">
      {isExtracting && (
        <div className="absolute -inset-2 bg-white/50 dark:bg-slate-950/50 backdrop-blur-sm z-10 flex items-center justify-center rounded-xl sm:rounded-2xl">
          <div className="flex flex-col items-center gap-2 text-emerald-600 dark:text-emerald-500 p-4 rounded-xl">
            <span className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
             <span className="text-sm font-semibold tracking-wide">
               {isExtracting === 'fetching' ? 'Fetching description...' : 'Extracting details...'}
             </span>
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="company" className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider mb-2 text-slate-700 dark:text-slate-400">
             <Building2 className="w-3.5 h-3.5 text-slate-700 dark:text-slate-400" /> Company
          </label>
          <input
            type="text"
            id="company"
            name="company"
            value={details.company || ''}
            onChange={handleChange}
            className="w-full text-sm border border-slate-200 dark:border-slate-800 rounded-lg px-3 py-2 bg-white/50 dark:bg-slate-900/50 hover:bg-slate-100 dark:hover:bg-slate-900 focus:bg-slate-100 dark:focus:bg-slate-900 dark:shadow-inner text-slate-700 dark:text-slate-300 placeholder:text-slate-500 focus:border-slate-200 dark:focus:border-slate-700 focus:ring-1 focus:ring-emerald-500 outline-none"
            placeholder="e.g. Acme Corp"
          />
        </div>
        <div>
           <label htmlFor="position" className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider mb-2 text-slate-700 dark:text-slate-400">
              <Briefcase className="w-3.5 h-3.5 text-slate-700 dark:text-slate-400" /> Position
           </label>
          <input
            type="text"
            id="position"
            name="position"
            value={details.position || ''}
            onChange={handleChange}
            className="w-full text-sm border border-slate-200 dark:border-slate-800 rounded-lg px-3 py-2 bg-white/50 dark:bg-slate-900/50 hover:bg-slate-100 dark:hover:bg-slate-900 focus:bg-slate-100 dark:focus:bg-slate-900 dark:shadow-inner text-slate-700 dark:text-slate-300 placeholder:text-slate-500 focus:border-slate-200 dark:focus:border-slate-700 focus:ring-1 focus:ring-emerald-500 outline-none"
            placeholder="e.g. Frontend Engineer"
          />
        </div>
        <div>
          <label htmlFor="targetLocation" className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider mb-2 text-slate-700 dark:text-slate-400">
             <MapPin className="w-3.5 h-3.5 text-slate-700 dark:text-slate-400" /> Target Location
          </label>
          <LocationInput 
            id="targetLocation"
            value={details.targetLocation || ''} 
            onChange={(val) => onChange({ ...details, targetLocation: val })}
          />
        </div>
        <div>
          <label htmlFor="jobType" className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider mb-2 text-slate-700 dark:text-slate-400">
            Job Type
          </label>
          <Select
            id="jobType"
            title="Job Type"
            value={details.jobType || ''}
            onChange={(val) => handleSelectChange('jobType', val)}
            options={[
              { value: 'full-time', label: 'Full-time' },
              { value: 'part-time', label: 'Part-time' },
              { value: 'contract', label: 'Contract' },
              { value: 'internship', label: 'Internship' }
            ]}
          />
        </div>
        <div>
          <label htmlFor="workModel" className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider mb-2 text-slate-700 dark:text-slate-400">
            Work Model
          </label>
          <Select
             id="workModel"
             title="Work Model"
             value={details.workModel || ''}
             onChange={(val) => handleSelectChange('workModel', val)}
             options={[
               { value: 'on-site', label: 'On-site' },
               { value: 'hybrid', label: 'Hybrid' },
               { value: 'remote-country', label: 'Remote - In Country' },
               { value: 'remote-global', label: 'Remote - Globally' }
             ]}
          />
        </div>
        
        <TargetFieldWithReference
          id="visaSponsorship"
          label="Visa Sponsorship"
          value={details.visaSponsorship || 'Unknown'}
          onChange={(val) => handleSelectChange('visaSponsorship', val)}
          reference={details.visaSponsorshipReference}
          link={details.visaSponsorshipLink}
          options={[
            { value: 'Yes', label: 'Yes' },
            { value: 'No', label: 'No' },
            { value: 'Unknown', label: 'Unknown (Not Found)' }
          ]}
        />
        
        <TargetFieldWithReference
          id="relocation"
          label="Relocation Support"
          value={details.relocation || 'Unknown'}
          onChange={(val) => handleSelectChange('relocation', val)}
          reference={details.relocationReference}
          link={details.relocationLink}
          options={[
            { value: 'Yes', label: 'Yes' },
            { value: 'No', label: 'No' },
            { value: 'Unknown', label: 'Unknown (Not Found)' }
          ]}
        />

        <TargetFieldWithReference
          id="salaryRange"
          label="Salary Range"
          value={details.salaryRange || ''}
          onChange={(val) => handleSelectChange('salaryRange', val)}
          reference={details.salaryRangeReference}
          link={details.salaryRangeLink}
          isInput={true}
          placeholder="e.g. $100k - $150k"
        />

        <div className="sm:col-span-2">
           <label htmlFor="requirements" className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider mb-2 text-slate-700 dark:text-slate-400">
             <FileText className="w-3.5 h-3.5 text-slate-700 dark:text-slate-400" /> Extra Notes / Requirements
           </label>
          <textarea
            id="requirements"
            name="requirements"
            value={details.requirements || ''}
            onChange={handleChange}
            className="w-full text-sm border border-slate-200 dark:border-slate-800 rounded-lg px-3 py-2 bg-white/50 dark:bg-slate-900/50 hover:bg-slate-100 dark:hover:bg-slate-900 focus:bg-slate-100 dark:focus:bg-slate-900 dark:shadow-inner text-slate-700 dark:text-slate-300 placeholder:text-slate-500 focus:border-slate-200 dark:focus:border-slate-700 focus:ring-1 focus:ring-emerald-500 outline-none resize-y min-h-[80px]"
            rows={2}
            placeholder="Any specific focus for this review?"
          />
        </div>
      </div>
    </div>
  );
});

